import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import { UserKeys, bulkShareContact } from '../../../../api/users-api';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
interface ShareContactProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function ShareContactDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: ShareContactProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [shareUsersProfiles, setShareUsersProfiles] = useState(true);
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: () =>
      bulkShareContact(
        'User',
        selectedUsers,
        shareUsersProfiles,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: shareUsersProfiles
          ? `The task to add ${selectedUsers.length} ${
              selectedUsers.length === 1 ? 'user' : 'users'
            } to the company directory has started.`
          : `The task to remove ${selectedUsers.length} ${
              selectedUsers.length === 1 ? 'user' : 'users'
            } from the company directory has started.`,
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.User, user] });
      });
      setChangeLogMessage('');
      setShareUsersProfiles(false);
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue removing the user(s) from the company directory. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_share_contact, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.BulkSetContactSharing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setShareUsersProfiles(false);
      }}
      title="Directory Settings"
      dialogContent={
        <Stack>
          <Typography sx={{ mb: 2 }}>
            {selectedUsers.length > 1 ? (
              <>
                Set Directory Settings for{' '}
                <strong>{selectedUsers.length} users</strong>.
              </>
            ) : (
              <>
                Set Directory Settings for <strong>{selectedUsers[0]}</strong>.
              </>
            )}
          </Typography>
          <FormControl>
            <RadioGroup
              aria-labelledby="share-contact-radio-buttons-group"
              name="radio-buttons-group"
              value={shareUsersProfiles}
              onChange={(e) => setShareUsersProfiles(e.target.value === 'true')}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Share to the company directory"
                sx={{ width: 'fit-content' }}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="Remove from the company directory"
                sx={{ width: 'fit-content' }}
              />
            </RadioGroup>
          </FormControl>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSaving}
          />
        </Stack>
      }
      primaryButton={{
        loading: isSaving,
        action: handleSubmit,
        disabled: isSaving,
        title: 'Update Directory',
      }}
    />
  );
}
